import React, { useState } from "react";
import {
  Grid,
  Typography,
  ButtonBase,
  Divider,
  makeStyles,
  CircularProgress,
  TextField,
} from "@material-ui/core";
import { useForm, Controller, useWatch } from "react-hook-form";
import { useQuery, useMutation } from "@apollo/react-hooks";
import gql from "graphql-tag";
import CloudUpload from "@material-ui/icons/CloudUpload";

import { useAlert } from "../../components/Alert";
import { Card, CardActions, CardContent } from "../../components/Card";
import Button from "../../components/Button";
import LoadingModal from "../../components/LoadingModal";

const GET_EXCLUSIVEWEB = gql`
  query exclusiveWeb($id: Int!) {
    exclusiveWeb(id: $id) {
      id
      siteTitle
      homePageTitle
      companyName
      mobile
      phone
      fax
      email
      address
      imageUrl
      url
    }
  }
`;

const SAVE_EXCLUSIVEWEB = gql`
  mutation saveExclusiveWeb($exclusiveWebInput: ExclusiveWebInput!) {
    saveExclusiveWeb(exclusiveWebInput: $exclusiveWebInput)
  }
`;

const UPLOAD_IMAGE = gql`
  mutation UploadImage($image: Upload!) {
    uploadImage(image: $image) {
      filename
      mimetype
      encoding
      location
    }
  }
`;

export default function CompanyDataTable({ id }) {
  const Alert = useAlert();
  const { handleSubmit, control, setValue, getValues } = useForm({
    defaultValues: {
      siteTitle: "",
      homePageTitle: "",
      companyName: "",
      mobile: "",
      phone: "",
      fax: "",
      email: "",
      address: "",
      imageUrl: "",
      url: "",
    },
  });
  const siteTitle = useWatch({ name: "siteTitle", control });
  const imageUrl = useWatch({ name: "imageUrl", control });

  const { loading } = useQuery(GET_EXCLUSIVEWEB, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    variables: {
      id,
    },
    onCompleted({ exclusiveWeb }) {
      if (exclusiveWeb) {
        const { __typename, ...newExclusiveWeb } = exclusiveWeb;
        for (const key in newExclusiveWeb) {
          setValue(`${key}`, newExclusiveWeb[key]);
        }
      }
    },
    onError(error) {
      Alert.alert("通知", `${error}`, [
        {
          text: "確定",
          type: "ok",
        },
      ]);
    },
  });

  const [saveExclusiveWeb, { loading: saveExclusiveWebLoading }] = useMutation(
    SAVE_EXCLUSIVEWEB,
    {
      onCompleted({ saveExclusiveWeb }) {
        if (saveExclusiveWeb) return Alert.notice(`儲存成功`);
        else return Alert.notice(`儲存失敗`);
      },
      onError(error) {
        return Alert.notice(`${error}`);
      },
    }
  );

  const [uploadImage, { loading: uploadImageLoading }] = useMutation(
    UPLOAD_IMAGE,
    {
      onCompleted({ uploadImage }) {
        setValue("imageUrl", uploadImage.location);
      },
      onError(error) {
        return Alert.notice(`${error}`);
      },
    }
  );

  function _save(data) {
    const { ...exclusiveWebInput } = data;
    exclusiveWebInput.id = id;
    saveExclusiveWeb({ variables: { exclusiveWebInput } });
  }

  if (loading) {
    return (
      <Grid container item justify="center">
        <CircularProgress color="secondary" />
      </Grid>
    );
  } else {
    return (
      <Card>
        <LoadingModal loading={uploadImageLoading || saveExclusiveWebLoading} />
        <CardContent>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="siteTitle"
                control={control}
                rules={{
                  required: "請填入網頁標題",
                }}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <Typography color="primary">網頁標題</Typography>
                    <TextField
                      {...field}
                      error={error}
                      helperText={error && error.message}
                      fullWidth
                    />
                  </>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="homePageTitle"
                control={control}
                rules={{
                  required: "請填入首頁標題",
                }}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <Typography color="primary">首頁標題</Typography>
                    <TextField
                      {...field}
                      error={error}
                      helperText={error && error.message}
                      fullWidth
                    />
                  </>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Controller
                name="companyName"
                control={control}
                rules={{
                  required: "請填入公司名稱",
                }}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <Typography color="primary">公司名稱</Typography>
                    <TextField
                      {...field}
                      error={error}
                      helperText={error && error.message}
                      fullWidth
                    />
                  </>
                )}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <Controller
                name="phone"
                control={control}
                rules={{
                  required: "請填入公司電話",
                }}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <Typography color="primary">公司電話</Typography>
                    <TextField
                      {...field}
                      error={error}
                      helperText={error && error.message}
                      fullWidth
                    />
                  </>
                )}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <Controller
                name="mobile"
                control={control}
                rules={{
                  required: "請填入聯絡電話",
                }}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <Typography color="primary">聯絡電話</Typography>
                    <TextField
                      {...field}
                      error={error}
                      helperText={error && error.message}
                      fullWidth
                    />
                  </>
                )}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <Controller
                name="fax"
                control={control}
                rules={{
                  required: "請填入傳真",
                }}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <Typography color="primary">傳真</Typography>
                    <TextField
                      {...field}
                      error={error}
                      helperText={error && error.message}
                      fullWidth
                    />
                  </>
                )}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <Controller
                name="email"
                control={control}
                rules={{
                  required: "請填入Email",
                }}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <Typography color="primary">Email</Typography>
                    <TextField
                      {...field}
                      error={error}
                      helperText={error && error.message}
                      fullWidth
                    />
                  </>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="address"
                control={control}
                rules={{
                  required: "請填入公司地址",
                }}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <Typography color="primary">公司地址</Typography>
                    <TextField
                      {...field}
                      error={error}
                      helperText={error && error.message}
                      fullWidth
                    />
                  </>
                )}
              />
            </Grid>
            <Grid item>
              <Typography color="primary">公司專屬網址</Typography>
              <Grid container alignItems="flex-end">
                <Grid item>
                  <Typography>https://new.5137.com.tw/</Typography>
                </Grid>
                <Grid item xs={3} sm={3}>
                  <Controller
                    name="url"
                    control={control}
                    rules={{
                      required: "請填入公司專屬網址",
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        variant={"standard"}
                        error={error}
                        helperText={error && error.message}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={3}>
              <Typography color="primary">公司照片</Typography>
              <ButtonBase
                for={`Carousel-image`}
                style={{
                  display: "flex",
                  height: 200,
                  justifyContent: "center",
                  alignItem: "center",
                  border: "1px solid",
                }}
                component="label"
              >
                {!!imageUrl ? (
                  <img
                    src={imageUrl}
                    style={{ height: "-webkit-fill-available" }}
                  />
                ) : (
                  <CloudUpload style={{ height: "inherit" }} />
                )}
              </ButtonBase>
              <input
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                id={`Carousel-image`}
                onChange={(event) => {
                  const {
                    target: { validity, files },
                  } = event;

                  if (validity.valid) {
                    for (let file of files) {
                      uploadImage({ variables: { image: file } });
                    }
                  }
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
        <Divider />
        <CardActions>
          <Grid container spacing={1} justify="center">
            <Grid item>
              <Button
                color="primary"
                variant="contained"
                onPress={handleSubmit(_save)}
              >
                儲存
              </Button>
            </Grid>
          </Grid>
        </CardActions>
      </Card>
    );
  }
}
